import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { studentApi } from '../../services/api';
import VideoWithSpeed from '../../components/VideoWithSpeed';
import SegmentedSwitch from '../../components/SegmentedSwitch';

// Where "Satın Al" goes when a recommended book's publisher carries no website
// of its own. The backend already substitutes this (env PUBLISHER_STORE_URL) so
// the mobile app gets the CTA too, but the payload type still admits null and a
// purchase button that goes nowhere is worse than one that goes to the catalog.
const PUBLISHER_STORE_URL = 'https://pleakademi.com/yayinlarimiz/';

// Digikoç page — podcast + Sınava Doğru video feeds. All three lists used
// to be hardcoded arrays; now they come from /student/digikoc, which reads
// content_items rows the admin panel uploaded via /admin/content/digikoc.
// The right-hand "queue" reuses the same source — recent items across both
// kinds — so it's always the latest content rather than a placeholder.

interface DigikocItem {
  id: string;
  kind: 'podcast' | 'sinava_dogru';
  title: string;
  thumbnail_url: string | null;
  duration: string | null;
  video_url: string | null;
  created_at: string;
  /** İçerik Yükle'de opsiyonel Ders seçilirse dolu — "Tüm Dersler" filtresini besler. */
  subject_id?: number | null;
  subject_name?: string | null;
}

// One watch strip per topic, already ordered by the backend: ders videoları →
// deneme çözümleri → kitap soru çözümleri, all from books the student owns.
type CoachMediaKind = 'lesson' | 'deneme' | 'kitap';
interface CoachMedia {
  kind: CoachMediaKind;
  title: string;
  book_title: string;
  question_number: number | null;
  video_url: string | null;
  thumbnail_url: string | null;
  /** The student answered this question (or one under this lesson) wrong. */
  missed: boolean;
}
const MEDIA_KIND_LABEL: Record<CoachMediaKind, string> = {
  lesson: 'Ders videosu',
  deneme: 'Deneme çözümü',
  kitap: 'Soru çözümü',
};
const MEDIA_KIND_COLOR: Record<CoachMediaKind, string> = {
  lesson: '#3973bf',
  deneme: '#7a07de',
  kitap: '#5b5b6b',
};
// The İzle step names what its strip holds once ("3 ders videosu, 4 soru
// çözümü"), so the tiles themselves need no kind label each.
const MEDIA_KIND_COUNT: Record<CoachMediaKind, string> = {
  lesson: 'ders videosu',
  deneme: 'deneme çözümü',
  kitap: 'soru çözümü',
};
// Same name whatever the case, punctuation or spacing ("EBOB-EKOK" = "Ebob - Ekok").
const sameName = (a: string, b: string) => {
  const key = (x: string) => x.toLocaleLowerCase('tr').replace(/[\s\-–—_.,:;!?'"()/]+/g, '');
  return key(a) === key(b);
};
// The marks on a topic card's path: watch → solve → the book to get.
const STEP_ICON = {
  watch: (
    <svg width="11" height="12" viewBox="0 0 10 12" fill="currentColor" aria-hidden="true"><path d="M1 0.8l8.4 5.2L1 11.2z" /></svg>
  ),
  solve: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  ),
  book: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 19.5V5a2 2 0 0 1 2-2h12a1 1 0 0 1 1 1v15" /><path d="M6 17h13" /><path d="M6 21h13" /><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17" />
    </svg>
  ),
};
interface CoachPreview {
  book_id: string;
  book_title: string;
  locked_count: number;
  cover_url: string | null;
  catalog_pdf_url: string | null;
  publisher_name: string | null;
  publisher_website: string | null;
}
// A card is a KONU (`bolum`: the bölüm its tests sit in — "EBOB - EKOK", never
// one of its tests or a single kazanım), or the whole book (`kitap`) when no konu
// can be named, as for old PLE attempts.
type FeedbackLevel = 'bolum' | 'kitap';
// A test of this konu the student has not solved yet ("çöz, başarını artır").
interface CoachPracticeTest {
  test_id: string;
  title: string;
  section_label: string;
  book_id: string;
  book_title: string;
  question_count: number;
}
// Weak and strong topics now share one shape — the backend enriches both with
// performance + solution videos, so a single card renderer serves both tabs.
interface CoachTopic {
  key: string;
  level: FeedbackLevel;
  level_label: string;
  title: string;
  subject_name: string | null;
  book_title: string | null;
  correct_count: number;
  wrong_count: number;
  total_count: number;
  accuracy_pct: number;
  media: CoachMedia[];
  previews: CoachPreview[];
  /** Weak konular only: `total` unsolved tests, the first few named. */
  practice?: { total: number; tests: CoachPracticeTest[] };
}
interface CoachPlan {
  student_name: string;
  student_grade: number | null;
  has_data: boolean;
  weak: CoachTopic[];
  strong: CoachTopic[];
}

const StudentDigikoc: React.FC = () => {
  const navigate = useNavigate();
  const [podcasts, setPodcasts] = useState<DigikocItem[]>([]);
  const [videos, setVideos] = useState<DigikocItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState<DigikocItem | null>(null);

  // Personalized coaching panel — lazily loaded. The header button renders
  // immediately; the plan is fetched only the first time the panel is expanded
  // (not on page open), so opening Digikoç never waits on this request.
  const [plan, setPlan] = useState<CoachPlan | null>(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [planRequested, setPlanRequested] = useState(false);
  // Solution-video lightbox (separate from the podcast/video `playing` modal
  // so the two feeds don't fight over one piece of state).
  const [coachVideo, setCoachVideo] = useState<{ title: string; url: string } | null>(null);
  // Collapsible — collapsed by default; expanding it triggers the lazy fetch.
  const [coachOpen, setCoachOpen] = useState(false);
  // Güçlü / Geliştirilecek pill toggle inside the coach panel — Güçlü opens by
  // default so the student is greeted with a win first.
  const [coachTab, setCoachTab] = useState<'strong' | 'weak'>('strong');
  // Topic keys whose extra book recommendations are expanded (first one always shows).
  const [openPreviews, setOpenPreviews] = useState<Record<string, boolean>>({});

  const toggleCoach = () => {
    setCoachOpen((o) => !o);
    if (!planRequested) {
      setPlanRequested(true);
      setPlanLoading(true);
      studentApi.digikocCoach()
        .then((r) => setPlan(r.data))
        .catch(() => setPlan(null))
        .finally(() => setPlanLoading(false));
    }
  };

  useEffect(() => {
    setLoading(true);
    studentApi.digikoc()
      .then((r) => {
        setPodcasts(r.data.podcasts ?? []);
        setVideos(r.data.videos ?? []);
      })
      .catch((e) => setError(e?.response?.data?.error ?? e.message))
      .finally(() => setLoading(false));
  }, []);

  // Right-hand queue = latest 6 items across both kinds. Falls back gracefully
  // to an empty list when the admin hasn't uploaded any digikoç content yet.
  const queue = useMemo(() => {
    return [...podcasts, ...videos]
      .sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at))
      .slice(0, 6);
  }, [podcasts, videos]);

  // "Tüm Dersler" — artık gerçek bir filtre: ders atanmış videolardan seçenek
  // listesi kurulur, seçim video kolonunu daraltır (eskiden tıklanamayan süs
  // bir kutuydu).
  const [dersFilter, setDersFilter] = useState<string>('');
  const dersOptions = useMemo(
    () => Array.from(new Set(videos.map((v) => v.subject_name).filter(Boolean))) as string[],
    [videos],
  );
  const filteredVideos = dersFilter ? videos.filter((v) => v.subject_name === dersFilter) : videos;
  // Aynı filtre podcast kolonu için de (bağımsız seçim).
  const [podFilter, setPodFilter] = useState<string>('');
  const podOptions = useMemo(
    () => Array.from(new Set(podcasts.map((p) => p.subject_name).filter(Boolean))) as string[],
    [podcasts],
  );
  const filteredPodcasts = podFilter ? podcasts.filter((p) => p.subject_name === podFilter) : podcasts;

  // A suggested test opens on its book page, selected and ready to start.
  const openPracticeTest = (t: CoachPracticeTest) =>
    navigate('/student/kitap-icerik', {
      state: { bookId: Number(t.book_id), autoOpenSection: t.section_label, autoOpenTestId: Number(t.test_id) },
    });

  // What Digikoç says between watching and solving on a weak konu: the next
  // move in one sentence, true to what the card holds. Null = nothing to add.
  const coachNote = (w: CoachTopic, practiceCount: number): { text: string; pose: 'solve' | 'think' } | null => {
    if (practiceCount > 0) {
      const lesson = w.media.some((m) => m.kind === 'lesson');
      const missed = w.media.some((m) => m.missed && m.kind !== 'lesson');
      if (lesson && missed) {
        return { pose: 'solve', text: 'Konu anlatımıyla başla, yanlış yaptığın soruların çözümüne bak, sonra bu testlerle kendini dene.' };
      }
      if (lesson) return { pose: 'solve', text: 'Önce konu anlatımını izle, sonra bu testlerle kendini dene.' };
      if (missed) return { pose: 'solve', text: 'Önce yanlış yaptığın soruların çözümüne bak, sonra bu testlerle kendini dene.' };
      return { pose: 'solve', text: 'Bu testlerle kendini dene. Her doğru cevap bu konudaki başarını yükseltir.' };
    }
    // A konu with nothing left to solve in the student's books: the videos are
    // what can still help.
    if (w.level === 'bolum' && w.media.length > 0) {
      return { pose: 'think', text: 'Bu konuda çözmediğin test kalmadı. Videolarla eksiklerini kapat.' };
    }
    return null;
  };

  // One topic card, read as a short study plan: where you stand, then İzle →
  // Çöz → the book to get, joined by a path, with the coach standing on it
  // between watching and solving. The score band (tone) is set once on the
  // card and coloured through CSS variables (index.css, .dk-card).
  const renderTopicCard = (w: CoachTopic, tone: 'weak' | 'strong') => {
    const strong = tone === 'strong';
    const band = strong ? 'high' : w.accuracy_pct < 50 ? 'low' : 'mid';
    const practice = w.practice?.tests ?? [];
    const practiceTotal = w.practice?.total ?? 0;
    const note = strong ? null : coachNote(w, practice.length);
    // The ders only when the book's own name does not already say it
    // ("9.Sınıf - Soru Bankası - Kimya" needs no "Kimya" in front).
    const lower = (x: string) => x.toLocaleLowerCase('tr');
    const subject = w.subject_name && !(w.book_title && lower(w.book_title).includes(lower(w.subject_name)))
      ? w.subject_name
      : null;
    const mediaSummary = (['lesson', 'deneme', 'kitap'] as const)
      .map((kind) => [kind, w.media.filter((m) => m.kind === kind).length] as const)
      .filter(([, n]) => n > 0)
      .map(([kind, n]) => `${n} ${MEDIA_KIND_COUNT[kind]}`)
      .join(', ');
    const hasSteps = w.media.length > 0 || practice.length > 0 || w.previews.length > 0;

    return (
      <article key={w.key} data-tone={band} className="dk-card rounded-[18px] border px-4 py-4 xl:px-6 xl:py-5">
        <header className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <h3 className="dk-konu text-[17px] xl:text-[21px] leading-tight">{w.title}</h3>
              {/* Every card is a konu, so only the exception is labelled: a card
                  for a whole book must not read as one konu. */}
              {w.level === 'kitap' && (
                <span className="text-[11px] font-semibold text-[#6b6878] bg-white border border-[#e7e4ee] rounded-full px-2 py-0.5">
                  Kitap geneli
                </span>
              )}
            </div>
            {/* The book is load-bearing, not decoration: section labels repeat
                across books, so two cards can share a title and differ only in
                the book they came from. */}
            {(subject || w.book_title) && (
              <p className="mt-1 text-[12px] xl:text-[13px] text-[#6b6878] truncate">
                {subject && <span className="font-semibold text-[#4a4658] mr-1.5">{subject}</span>}
                {w.book_title}
              </p>
            )}
          </div>
          <div className="flex-shrink-0 text-right">
            <p className="dk-score text-[26px] xl:text-[32px] leading-none">%{w.accuracy_pct}</p>
            <p className="mt-1.5 text-[12px] xl:text-[13px] text-[#6b6878] whitespace-nowrap">
              {w.correct_count}/{w.total_count} doğru
            </p>
          </div>
        </header>
        <div className="dk-meter mt-3" aria-hidden="true">
          <span style={{ width: `${Math.max(0, Math.min(100, w.accuracy_pct))}%` }} />
        </div>

        {hasSteps && (
          <ol className="dk-path mt-5 xl:mt-6">
            {/* İzle — everything playable from the student's own books, in the
                backend's order: the lesson that teaches the konu, then deneme
                solutions, then book solutions. */}
            {w.media.length > 0 && (
              <li className="dk-step">
                <span className="dk-step__mark" aria-hidden="true">{STEP_ICON.watch}</span>
                <div className="dk-step__head">
                  <h4 className="dk-step__title">İzle</h4>
                  <p className="dk-step__meta">{mediaSummary}</p>
                </div>
                <div className="dk-strip -mr-4 xl:-mr-6 pr-4 xl:pr-6 mt-3 flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory">
                  {w.media.map((m, i) => renderMediaTile(w, m, i))}
                </div>
              </li>
            )}

            {/* The coach, standing on the path between watching and solving. */}
            {note && (
              <li className="dk-step dk-step--coach">
                <img
                  src={note.pose === 'solve' ? '/a-dk-coach-solve.png' : '/a-dk-coach-think.png'}
                  alt=""
                  className="dk-coach"
                />
                <p className="dk-bubble">{note.text}</p>
              </li>
            )}

            {/* Çöz — the konu's tests the student has not solved yet. Each one
                opens on the book page, ready to start; solving them is what
                moves this konu's score. */}
            {practice.length > 0 && (
              <li className="dk-step">
                <span className="dk-step__mark" aria-hidden="true">{STEP_ICON.solve}</span>
                <div className="dk-step__head">
                  <h4 className="dk-step__title">Çöz, başarını artır</h4>
                  <p className="dk-step__meta">{practiceTotal} çözülmemiş test</p>
                </div>
                <ul className="mt-3 max-w-[640px] space-y-2">
                  {practice.map((t) => (
                    <li key={t.test_id}>
                      <button type="button" onClick={() => openPracticeTest(t)} className="dk-test group">
                        <span className="flex-1 min-w-0">
                          <span className="block text-[13px] xl:text-[14px] font-semibold text-[#24212b] truncate">{t.title}</span>
                          {t.book_title !== w.book_title && (
                            <span className="block text-[11px] xl:text-[12px] text-[#6b6878] truncate">{t.book_title}</span>
                          )}
                        </span>
                        <span className="flex-shrink-0 text-[12px] xl:text-[13px] text-[#6b6878]">{t.question_count} soru</span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="dk-test__go flex-shrink-0" aria-hidden="true">
                          <path d="M9 6l6 6-6 6" />
                        </svg>
                      </button>
                    </li>
                  ))}
                </ul>
                {practiceTotal > practice.length && (
                  <p className="mt-2.5 text-[12px] xl:text-[13px] text-[#6b6878]">
                    ve {practiceTotal - practice.length} test daha seni bekliyor
                  </p>
                )}
              </li>
            )}

            {/* Kitap — in-grade books the student does NOT own: the gap in the
                shelf the strip above plays from. Weak konular only (the backend
                sends none for strong). The best match shows; the rest wait
                behind one toggle. Satın Al → publisher store (platform store
                fallback server-side), Örnek sayfalar → catalog sample PDF. */}
            {w.previews.length > 0 && (
              <li className="dk-step">
                <span className="dk-step__mark" aria-hidden="true">{STEP_ICON.book}</span>
                <div className="dk-step__head">
                  <h4 className="dk-step__title">Kitaplığında yok</h4>
                  <p className="dk-step__meta">Bu konuya çalışmak için Digikoç önerisi</p>
                </div>
                <div className="mt-3 space-y-2.5">
                  {(openPreviews[w.key] ? w.previews : w.previews.slice(0, 1)).map((p, i) => (
                    <div key={i} className="rec-card rec-shelf rounded-[14px] p-3.5 xl:p-4">
                      <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
                        <div className="flex items-center gap-3.5 xl:gap-4 flex-1 min-w-0">
                          {/* The book, standing on a shelf ledge. */}
                          <div className="flex-shrink-0 w-[50px] xl:w-[56px]">
                            <div className="rec-book relative w-full rounded-[3px] overflow-hidden shadow-[0_6px_14px_-6px_rgba(0,0,0,0.7)]" style={{ aspectRatio: '3/4' }}>
                              {p.cover_url
                                ? <img src={p.cover_url} alt="" className="w-full h-full object-cover" />
                                : <div className="w-full h-full bg-[#3a2c63]" />}
                              {/* Spine edge — what turns a flat thumbnail into a book. */}
                              <div className="rec-book__spine absolute inset-y-0 left-0 w-[6px]" />
                            </div>
                            <div className="rec-book__shelf h-[2px] mt-1.5 rounded-full" />
                          </div>
                          <div className="min-w-0">
                            <p className="rec-title text-[15px] xl:text-[17px] text-white leading-snug">{p.book_title}</p>
                            {p.publisher_name && (
                              <p className="text-[11px] xl:text-[12px] font-semibold text-[#b9afd6] mt-0.5">{p.publisher_name}</p>
                            )}
                            {/* Says the one thing the count doesn't: these videos
                                are for THIS konu, not the book at large. */}
                            <p className="text-[12px] text-[#d8d0ec] mt-1.5">Tamamı bu konunun soruları.</p>
                          </div>
                        </div>

                        {/* The count is the product: it's the reason to buy, so it gets the size. */}
                        <div className="flex items-end gap-2 flex-shrink-0">
                          <span className="rec-count text-[30px] xl:text-[34px]">{p.locked_count}</span>
                          <span className="text-[11px] xl:text-[12px] text-[#b9afd6] leading-tight pb-0.5">
                            çözümlü<br />soru videosu
                          </span>
                        </div>

                        <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                          <a
                            href={p.publisher_website ?? PUBLISHER_STORE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="rec-buy inline-flex items-center gap-1.5 rounded-full pl-3.5 pr-3 py-2 text-[12px] xl:text-[13px] font-bold text-white"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <circle cx="9" cy="20" r="1.4" /><circle cx="18" cy="20" r="1.4" />
                              <path d="M2 3h2.2l2.3 11.2a2 2 0 0 0 2 1.6h7.9a2 2 0 0 0 2-1.55L21 7H5.5" />
                            </svg>
                            Satın Al
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="opacity-70" aria-hidden="true">
                              <path d="M7 17L17 7" /><path d="M8 7h9v9" />
                            </svg>
                          </a>
                          {p.catalog_pdf_url && (
                            <a
                              href={p.catalog_pdf_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="rec-ghost inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[12px] xl:text-[13px] font-semibold"
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M4 19.5V5a2 2 0 0 1 2-2h11a1 1 0 0 1 1 1v15" /><path d="M6 17h12" /><path d="M6 21h12a2 2 0 0 1-2-2v-2" />
                              </svg>
                              Örnek sayfalar
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {w.previews.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setOpenPreviews((o) => ({ ...o, [w.key]: !o[w.key] }))}
                    aria-expanded={!!openPreviews[w.key]}
                    className="mt-2 text-[12px] font-semibold text-[#7a07de] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#7a07de] rounded"
                  >
                    {openPreviews[w.key] ? 'Daha az göster' : `${w.previews.length - 1} kitap önerisi daha`}
                  </button>
                )}
              </li>
            )}
          </ol>
        )}

        {/* Nothing to watch, solve or pick up. A strong topic needs no apology;
            a weak one says what is true today instead of promising content. */}
        {!strong && !hasSteps && (
          <p className="mt-4 text-[12px] xl:text-[13px] text-[#6b6878]">
            Kitaplığındaki kitaplarda bu konuya ait video yok. Konuyu kitabından tekrar etmen iyi olur.
          </p>
        )}
      </article>
    );
  };

  // A tile in the İzle strip. The picture carries the kind (a lesson is set on
  // the brand gradient, a deneme solution is tagged), so the caption under it
  // only has to say which question it is and whether the student missed it.
  const renderMediaTile = (w: CoachTopic, m: CoachMedia, i: number) => {
    const lesson = m.kind === 'lesson';
    const label = MEDIA_KIND_LABEL[m.kind];
    const color = MEDIA_KIND_COLOR[m.kind];
    const question = m.question_number != null ? `Soru ${m.question_number}` : label;
    // The card header already names the book; repeat it only when it differs.
    const otherBook = m.book_title !== w.book_title ? m.book_title : '';
    const missed = m.missed && !lesson;
    // A soru bankası names its tests after the konu; under that konu's card the
    // name says nothing, so it is left out.
    const testName = m.title && !sameName(m.title, w.title) ? m.title : '';
    const modalTitle = lesson
      ? `${m.title} · ${m.book_title}`
      : `${w.title} · ${m.book_title}${m.question_number != null ? ` · Soru ${m.question_number}` : ''}`;
    return (
      <button
        key={`${m.kind}-${i}`}
        type="button"
        onClick={() => m.video_url && setCoachVideo({ title: modalTitle, url: m.video_url })}
        aria-label={`${label}: ${lesson ? m.title : `${question}, ${m.title}`}${missed ? ', yanlış yaptığın soru' : ''}, ${m.book_title}`}
        className="dk-tile snap-start flex-shrink-0 w-[168px] xl:w-[196px] text-left rounded-[12px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3973bf]"
      >
        <div
          className={`dk-tile__frame relative w-full rounded-[12px] overflow-hidden ${lesson ? 'dk-lesson' : 'bg-white'} ${missed ? 'dk-tile__frame--missed' : ''}`}
          style={{ aspectRatio: '16/10' }}
        >
          {lesson ? (
            // A lesson has no question image; its title is the picture.
            <>
              <p className="absolute left-3 top-2.5 text-[11px] font-semibold text-white/80">Ders videosu</p>
              <p className="absolute inset-x-0 bottom-0 p-3 pr-11 text-white text-[13px] xl:text-[14px] font-bold leading-snug line-clamp-2">{m.title}</p>
            </>
          ) : (
            <>
              {/* Shown when the question image is missing or fails to load —
                  the question number is the next most recognisable thing. */}
              <p className="absolute inset-0 flex items-center justify-center text-[18px] xl:text-[20px] font-extrabold" style={{ color }}>
                {question}
              </p>
              {m.thumbnail_url && (
                <img
                  src={m.thumbnail_url}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover bg-white"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              )}
              {m.kind === 'deneme' && (
                <span className="absolute left-2 top-2 text-[10px] xl:text-[11px] font-semibold text-white bg-[#7a07de] rounded-full px-2 py-0.5">
                  Deneme
                </span>
              )}
            </>
          )}
          <span className="dk-tile__play absolute right-2 bottom-2 w-8 h-8 rounded-full bg-white shadow-[0_4px_10px_-4px_rgba(36,33,43,0.45)] flex items-center justify-center">
            <svg width="10" height="12" viewBox="0 0 10 12" fill={color} aria-hidden="true"><path d="M1 0.8l8.4 5.2L1 11.2z" /></svg>
          </span>
        </div>
        {lesson ? (
          otherBook && <p className="mt-2 text-[12px] text-[#6b6878] truncate">{otherBook}</p>
        ) : (
          <>
            <p className="mt-2 flex items-baseline gap-2 text-[12px] xl:text-[13px] leading-tight">
              <span className="font-semibold text-[#24212b]">{question}</span>
              {missed && <span className="font-semibold text-[#d63f86]">Yanlış yaptığın</span>}
            </p>
            {testName && <p className="mt-0.5 text-[11px] xl:text-[12px] text-[#6b6878] truncate">{testName}</p>}
          </>
        )}
      </button>
    );
  };

  const renderItemCard = (it: DigikocItem, accent: 'pink' | 'blue') => {
    const ring = accent === 'pink' ? 'hover:border-[#e5599b]' : 'hover:border-[#3973bf]';
    const chip = accent === 'pink' ? 'bg-[#e5599b]/10 text-[#e5599b]' : 'bg-[#3973bf]/10 text-[#3973bf]';
    const playBg = accent === 'pink' ? 'bg-[#e5599b]' : 'bg-[#3973bf]';
    return (
      <button
        key={it.id}
        onClick={() => it.video_url && setPlaying(it)}
        className={`group w-full text-left flex items-center gap-3 rounded-[12px] p-2.5 border border-gray-100 bg-white ${ring} hover:shadow-sm transition-all`}
      >
        {/* Thumbnail with hover play affordance — fixed size so a long title
            never stretches the row (min-w-0 on the text side does the rest). */}
        <div className="relative w-14 h-14 xl:w-16 xl:h-16 rounded-[10px] overflow-hidden bg-gray-100 flex-shrink-0">
          {it.thumbnail_url
            ? <img src={it.thumbnail_url} alt="" className="w-full h-full object-cover" />
            : <div className="w-full h-full bg-gray-100" />}
          <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/25 transition-colors">
            <span className={`w-7 h-7 rounded-full ${playBg} shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity`}>
              <svg width="9" height="11" viewBox="0 0 10 12" fill="white"><path d="M0 0l10 6-10 6z" /></svg>
            </span>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] xl:text-[15px] font-semibold text-[#2b2b2b] leading-snug line-clamp-2">{it.title}</p>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            {it.subject_name && (
              <span className={`text-[10px] xl:text-[11px] font-semibold rounded-full px-2 py-0.5 ${chip}`}>{it.subject_name}</span>
            )}
            {it.duration && (
              <span className="inline-flex items-center gap-1 text-[10px] xl:text-[12px] text-gray-500">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
                {it.duration}
              </span>
            )}
          </div>
        </div>
      </button>
    );
  };

  return (
    <div>
      <div className="mb-3">
        <h1 className="text-[18px] xl:text-[24px] font-semibold text-[#2b2b2b]">Digikoç</h1>
        <p className="text-[12px] xl:text-[14px] text-gray-500 mt-0.5">Kişisel koçun, çözümlü videolar ve sınava hazırlık serileri bir arada.</p>
      </div>

      {/* Sana Özel Koçluk — the page's signature CTA. An animated wavy brand
          gradient with twinkling sparkles + a floating mascot pulls the eye here
          first. Clicking expands the personalized coaching (lazy-fetched). */}
      <div className="mb-3 rounded-[16px] overflow-hidden shadow-md border border-gray-100 bg-white">
        <button
          type="button"
          onClick={toggleCoach}
          aria-expanded={coachOpen}
          className="dk-hero relative w-full overflow-hidden px-4 py-4 xl:px-7 xl:py-6 flex items-center gap-3 xl:gap-5 text-left cursor-pointer transition-transform hover:scale-[1.004]"
        >
          {/* twinkling sparkles */}
          {[
            { top: '16%', left: '34%', s: 10, d: '0s' },
            { top: '64%', left: '28%', s: 8, d: '0.5s' },
            { top: '22%', left: '66%', s: 13, d: '1s' },
            { top: '70%', left: '82%', s: 9, d: '1.4s' },
            { top: '46%', left: '50%', s: 7, d: '0.8s' },
          ].map((sp, i) => (
            <svg key={i} className="dk-sparkle absolute text-white/90 pointer-events-none" width={sp.s} height={sp.s} viewBox="0 0 24 24" fill="currentColor" style={{ top: sp.top, left: sp.left, animationDelay: sp.d }}>
              <path d="M12 0l2.6 9.4L24 12l-9.4 2.6L12 24l-2.6-9.4L0 12l9.4-2.6z" />
            </svg>
          ))}
          {/* floating mascot */}
          <div className="relative z-10 min-w-0 flex-1">
            <p className="text-white font-extrabold text-[19px] xl:text-[28px] leading-tight drop-shadow-sm">Sana Özel Koçluk</p>
            <p className="text-white/90 text-[11px] xl:text-[15px] leading-snug mt-0.5">
              Güçlü ve geliştirmen gereken konuların; her biri için ders videoları ve soru çözümleri.
            </p>
          </div>
          <div className="relative z-10 flex-shrink-0 flex items-center gap-2">
            <span className="hidden xl:inline text-white font-bold text-[14px]">{coachOpen ? 'Kapat' : 'Aç'}</span>
            <span className="w-9 h-9 xl:w-11 xl:h-11 rounded-full bg-white/25 flex items-center justify-center">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.4"
                className={`transition-transform duration-200 ${coachOpen ? '' : '-rotate-90'}`}>
                <path d="M6 9l6 6 6-6" />
              </svg>
            </span>
          </div>
        </button>

        {coachOpen && (
          <div className="p-3 xl:p-4">
            {planLoading ? (
              <div className="flex justify-center py-8">
                <div className="w-6 h-6 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : plan && plan.has_data ? (
              <div>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  {/* Güçlü / Geliştirilecek pill toggle — Güçlü opens by default. */}
                  <SegmentedSwitch
                    size="sm"
                    ariaLabel="Koç önerileri"
                    value={coachTab}
                    onChange={setCoachTab}
                    options={[
                      { value: 'strong', label: `Güçlü Konuların (${plan.strong.length})`, color: '#10b981' },
                      { value: 'weak', label: `Geliştirmen Gereken (${plan.weak.length})`, color: '#e5599b' },
                    ]}
                  />
                  {/* The written Digikoç comment lives on Performansım. */}
                  <Link
                    to="/student/performansim?odak=yorum"
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#f0d0e0] bg-[#f7edf3] pl-1 pr-3 py-1 text-[12px] xl:text-[13px] font-bold text-[#e5599b] hover:border-[#e5599b] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#e5599b]"
                  >
                    <img src="/a-digikoc-mascot.png" alt="" className="w-6 h-6 object-contain" />
                    Digikoç yorumunu oku
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
                  </Link>
                </div>

                {coachTab === 'strong' ? (
                  plan.strong.length === 0 ? (
                    <div className="flex items-center gap-3 xl:gap-4 rounded-[14px] border border-emerald-100 bg-emerald-50 p-4">
                      <img src="/a-digikoc-mascot.png" alt="" className="w-14 h-14 xl:w-16 xl:h-16 object-contain flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-[14px] xl:text-[16px] font-bold text-emerald-700">Güçlü konu rozetlerin yolda! 🚀</p>
                        <p className="text-[12px] xl:text-[13px] text-emerald-600/80 mt-0.5">Çözdükçe en iyi olduğun konular burada belirecek. Çözmeye devam et!</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Celebratory well-done banner — greets the student with a win. */}
                      <div className="relative overflow-hidden flex items-center gap-3 xl:gap-4 rounded-[14px] p-4 bg-gradient-to-r from-emerald-500 to-teal-500">
                        <img src="/a-register-success-mascot.png" alt="" className="dk-float w-16 h-16 xl:w-20 xl:h-20 object-contain flex-shrink-0 drop-shadow-lg" />
                        <div className="min-w-0">
                          <p className="text-white font-extrabold text-[16px] xl:text-[20px] leading-tight">Aferin{plan.student_name ? `, ${plan.student_name}` : ''}!</p>
                          <p className="text-white/90 text-[12px] xl:text-[14px] mt-0.5">{plan.strong.length} konuda güçlüsün. Zirvede kalmak için videolarla pekiştir.</p>
                        </div>
                      </div>
                      <div className="space-y-4">
                        {plan.strong.map((s) => renderTopicCard(s, 'strong'))}
                      </div>
                    </div>
                  )
                ) : (
                  plan.weak.length === 0 ? (
                    <div className="flex items-center gap-3 xl:gap-4 rounded-[14px] border border-emerald-100 bg-emerald-50 p-4">
                      <img src="/a-register-success-mascot.png" alt="" className="w-14 h-14 xl:w-16 xl:h-16 object-contain flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-[14px] xl:text-[16px] font-bold text-emerald-700">Harika! Şu an geliştirmen gereken bir konu yok 🎉</p>
                        <p className="text-[12px] xl:text-[13px] text-emerald-600/80 mt-0.5">Böyle devam! Yeni sorular çözdükçe buradan takip edebilirsin.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Motivating (not disappointing) banner — smiling mascot,
                          warm gradient, "let's work on these" framing. */}
                      <div className="relative overflow-hidden flex items-center gap-3 xl:gap-4 rounded-[14px] p-4 bg-gradient-to-r from-[#e5599b] to-[#f59e0b]">
                        <img src="/a-digikoc-mascot.png" alt="" className="dk-float w-16 h-16 xl:w-20 xl:h-20 object-contain flex-shrink-0 drop-shadow-lg" />
                        <div className="min-w-0">
                          <p className="text-white font-extrabold text-[16px] xl:text-[20px] leading-tight">Hadi bunları da güçlendirelim!</p>
                          <p className="text-white/90 text-[12px] xl:text-[14px] mt-0.5">Bu {plan.weak.length} konuya biraz daha çalış. Önce ders videosunu izle, sonra çözümlere geç.</p>
                        </div>
                      </div>
                      <div className="space-y-4">
                        {plan.weak.map((w) => renderTopicCard(w, 'weak'))}
                      </div>
                    </div>
                  )
                )}
              </div>
            ) : plan && !plan.has_data ? (
              <p className="text-[12px] xl:text-[13px] text-gray-500 text-center py-2">
                Konu bazlı koçluk için önce kitaplardan soru çözmelisin. Çözdükçe zayıf ve güçlü konuların burada görünecek.
              </p>
            ) : (
              <p className="text-center text-red-400 py-4 text-sm">Koçluk verisi yüklenemedi.</p>
            )}
          </div>
        )}
      </div>

      <div className="flex gap-3 mb-3" style={{ aspectRatio: '1493/381' }}>
        <div className="w-[56.5%] rounded-[16px] overflow-hidden">
          <img src="/a-dk-banner-full.png" alt="Digikoç" className="w-full h-full object-cover" />
        </div>
        <div className="w-[43.5%] rounded-[16px] overflow-hidden">
          {/* Statik player görseli yerine gerçek içerik: İzleme/Dinleme
              Sırası'nın ilk öğesi — tıklayınca oynatıcı açılır. Sıra boşken
              eski görsel geri gelir. */}
          {queue.length > 0 && queue[0].video_url ? (
            <button
              onClick={() => setPlaying(queue[0])}
              title={queue[0].title}
              className="relative block w-full h-full cursor-pointer group text-left overflow-hidden"
            >
              {/* absolute fill: kapak görselinin doğal boyutu satırı şişirmesin
                  (dev dikey kapak tüm sayfayı büyütüyordu) — akıştan çıkar. */}
              <img
                src={queue[0].thumbnail_url || '/a-dk-video-full.png'}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-4">
                <div className="w-11 h-11 xl:w-14 xl:h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                  <svg width="16" height="18" viewBox="0 0 10 12" fill="#3973bf" className="ml-0.5"><path d="M0 0l10 6-10 6z"/></svg>
                </div>
                <p className="max-w-full truncate text-center text-white text-[12px] xl:text-[15px] font-semibold drop-shadow">
                  {queue[0].title}
                </p>
              </div>
            </button>
          ) : (
            <img src="/a-dk-video-full.png" alt="Video" className="w-full h-full object-cover" />
          )}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-3">
        {/* Podcast + Sınava Doğru feeds — deliberately calm, flat cards (no
            gradient/animation) so the animated hero above stays the one focal
            point. Real styled headers replace the old PNG tabs, and every flex
            child carries min-w-0 so long titles clamp instead of overflowing. */}
        <div className="flex-1 min-w-0 rounded-[16px] border border-gray-100 bg-white overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <p className="text-center text-red-400 py-8">{error}</p>
          ) : (
            <div className="flex flex-col lg:flex-row lg:divide-x divide-gray-100">
              {/* Digikoç Podcastler */}
              <div className="flex-1 min-w-0 p-3 xl:p-4 border-b lg:border-b-0 border-gray-100">
                <div className="flex items-center gap-2 mb-3 min-w-0">
                  <span className="w-8 h-8 rounded-full bg-[#e5599b]/10 flex items-center justify-center flex-shrink-0">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#e5599b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="2" width="6" height="12" rx="3" /><path d="M5 10a7 7 0 0014 0" /><line x1="12" y1="19" x2="12" y2="22" /></svg>
                  </span>
                  <p className="text-[14px] xl:text-[16px] font-bold text-[#2b2b2b] truncate">Digikoç Podcastler</p>
                </div>
                <select
                  value={podFilter}
                  onChange={(e) => setPodFilter(e.target.value)}
                  className="w-full bg-gray-50 rounded-[10px] px-3 py-2 mb-3 border border-gray-200 text-[13px] xl:text-[14px] font-medium text-[#2b2b2b] cursor-pointer focus:outline-none focus:border-[#e5599b]"
                >
                  <option value="">Tüm Dersler</option>
                  {podOptions.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                <div className="space-y-2">
                  {podcasts.length === 0 ? (
                    <p className="text-gray-400 text-sm text-center py-8">Henüz podcast yüklenmedi.</p>
                  ) : filteredPodcasts.length === 0 ? (
                    <p className="text-gray-400 text-sm text-center py-8">Bu derste podcast yok.</p>
                  ) : (
                    filteredPodcasts.map((t) => renderItemCard(t, 'pink'))
                  )}
                </div>
              </div>
              {/* Sınava Doğru Video Serisi */}
              <div className="flex-1 min-w-0 p-3 xl:p-4">
                <div className="flex items-center gap-2 mb-3 min-w-0">
                  <span className="w-8 h-8 rounded-full bg-[#3973bf]/10 flex items-center justify-center flex-shrink-0">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="#3973bf"><path d="M8 5v14l11-7z" /></svg>
                  </span>
                  <p className="text-[14px] xl:text-[16px] font-bold text-[#2b2b2b] truncate">Sınava Doğru Video Serisi</p>
                </div>
                <select
                  value={dersFilter}
                  onChange={(e) => setDersFilter(e.target.value)}
                  className="w-full bg-gray-50 rounded-[10px] px-3 py-2 mb-3 border border-gray-200 text-[13px] xl:text-[14px] font-medium text-[#2b2b2b] cursor-pointer focus:outline-none focus:border-[#3973bf]"
                >
                  <option value="">Tüm Dersler</option>
                  {dersOptions.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                <div className="space-y-2">
                  {videos.length === 0 ? (
                    <p className="text-gray-400 text-sm text-center py-8">Henüz video yüklenmedi.</p>
                  ) : filteredVideos.length === 0 ? (
                    <p className="text-gray-400 text-sm text-center py-8">Bu derste video yok.</p>
                  ) : (
                    filteredVideos.map((v) => renderItemCard(v, 'blue'))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* İzleme / Dinleme Sırası — clean styled header replaces the PNG. */}
        <div className="w-full lg:w-[340px] flex-shrink-0 hidden lg:block">
          <div className="rounded-[16px] border border-gray-100 bg-white overflow-hidden h-full">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
              <span className="w-8 h-8 rounded-full bg-[#59e56e]/15 flex items-center justify-center flex-shrink-0">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#2fae4e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><circle cx="3.5" cy="6" r="1" /><circle cx="3.5" cy="12" r="1" /><circle cx="3.5" cy="18" r="1" /></svg>
              </span>
              <p className="text-[14px] xl:text-[16px] font-bold text-[#2b2b2b]">İzleme / Dinleme Sırası</p>
            </div>
            <div className="p-3 xl:p-4 space-y-2 min-h-[200px]">
              {loading ? (
                <p className="text-gray-400 text-sm text-center py-8">Yükleniyor…</p>
              ) : queue.length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-8">Yakında eklenecek.</p>
              ) : (
                queue.map((q) => (
                  <button
                    key={q.id}
                    onClick={() => q.video_url && setPlaying(q)}
                    className="group w-full text-left flex items-center gap-2.5 rounded-[10px] p-2.5 border border-gray-100 bg-white hover:border-[#59e56e] hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-9 h-9 rounded-full bg-[#59e56e]/15 group-hover:bg-[#59e56e] flex items-center justify-center flex-shrink-0 transition-colors">
                      <svg width="9" height="11" viewBox="0 0 10 12" className="fill-[#2fae4e] group-hover:fill-white transition-colors"><path d="M0 0l10 6-10 6z" /></svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] xl:text-[14px] font-semibold text-[#2b2b2b] truncate">{q.title}</p>
                      {q.duration && <p className="text-[10px] xl:text-[12px] text-gray-500">{q.duration}</p>}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {playing && playing.video_url && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center p-4" onClick={() => setPlaying(null)}>
          <div className="relative w-full max-w-[900px]" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setPlaying(null)} className="absolute -top-10 right-0 text-white text-xl">✕ Kapat</button>
            <VideoWithSpeed autoPlay src={playing.video_url} className="w-full rounded-[12px] bg-black" />
            <p className="text-white text-center mt-3">{playing.title}</p>
          </div>
        </div>
      )}

      {coachVideo && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center p-4" onClick={() => setCoachVideo(null)}>
          <div className="relative w-full max-w-[900px]" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setCoachVideo(null)} className="absolute -top-10 right-0 text-white text-xl">✕ Kapat</button>
            <VideoWithSpeed autoPlay src={coachVideo.url} className="w-full rounded-[12px] bg-black" />
            <p className="text-white text-center mt-3">{coachVideo.title}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentDigikoc;
